import { useCallback, useEffect, useRef, useState } from "react";

export type SpeechInputErrorCode =
  | "not-allowed"
  | "service-not-allowed"
  | "audio-capture"
  | "no-speech"
  | "aborted"
  | "ended-immediately"
  | "start-failed"
  | "unsupported"
  | "unknown";

export interface SpeechInputErrorState {
  code: SpeechInputErrorCode;
  message: string;
}

export interface SpeechInputStartResult {
  ok: boolean;
  code?: SpeechInputErrorCode;
  message?: string;
}

type Opts = {
  lang?: string;
  onFinal: (text: string) => void;
  onInterim?: (text: string) => void;
  onError?: (error: SpeechInputErrorState) => void;
  preflightMicrophone?: boolean;
  restartOnUnexpectedEnd?: boolean;
};

function buildSpeechError(code: SpeechInputErrorCode, message?: string): SpeechInputErrorState {
  switch (code) {
    case "not-allowed":
    case "service-not-allowed":
      return {
        code,
        message: message ?? "Microphone permission was blocked. Allow microphone access and try dictation again.",
      };
    case "audio-capture":
      return {
        code,
        message: message ?? "No microphone input was available. Check the active input device and browser permissions.",
      };
    case "no-speech":
      return {
        code,
        message: message ?? "No speech was detected. Try again after the microphone is active.",
      };
    case "aborted":
      return {
        code,
        message: message ?? "Dictation was interrupted before speech could be captured.",
      };
    case "ended-immediately":
      return {
        code,
        message: message ?? "Dictation ended immediately. Check microphone permission or browser speech support.",
      };
    case "start-failed":
      return {
        code,
        message: message ?? "Dictation could not start in this browser session.",
      };
    case "unsupported":
      return {
        code,
        message: message ?? "This browser does not expose live speech recognition.",
      };
    default:
      return {
        code: "unknown",
        message: message ?? "Dictation failed for an unknown reason.",
      };
  }
}

export function useSpeechInput({
  lang = "en-US",
  onFinal,
  onInterim,
  onError,
  preflightMicrophone = false,
  restartOnUnexpectedEnd = false,
}: Opts) {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(false);
  const [error, setError] = useState<SpeechInputErrorState | null>(null);
  const recRef = useRef<SpeechRecognition | null>(null);
  const manualStopRef = useRef(false);
  const startTsRef = useRef(0);
  const lastFinalTsRef = useRef(0);
  const restartBudgetRef = useRef(0);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  useEffect(() => {
    const SR =
      window.webkitSpeechRecognition ??
      window.SpeechRecognition ??
      null;
    if (!SR) {
      setError(buildSpeechError("unsupported"));
      return;
    }
    setSupported(true);
    const rec = new SR();
    rec.lang = lang;
    rec.interimResults = true;
    rec.continuous = true;

    rec.onresult = (e: SpeechRecognitionEvent) => {
      let interim = "";
      let final = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const text = e.results[i][0].transcript;
        if (e.results[i].isFinal) final += text;
        else interim += text;
      }
      if (interim || final) setError(null);
      if (interim && onInterim) onInterim(interim);
      if (final) {
        lastFinalTsRef.current = Date.now();
        onFinal(final);
      }
    };
    rec.onend = () => {
      setListening(false);

      const wasManualStop = manualStopRef.current;
      manualStopRef.current = false;
      if (wasManualStop) return;

      const now = Date.now();
      const sessionAgeMs = now - startTsRef.current;
      const hadRecentFinal = now - lastFinalTsRef.current < 1200;

      if (restartOnUnexpectedEnd && restartBudgetRef.current > 0 && sessionAgeMs < 1200 && !hadRecentFinal) {
        restartBudgetRef.current -= 1;
        window.setTimeout(() => {
          try {
            rec.start();
            startTsRef.current = Date.now();
            setListening(true);
          } catch {
            const nextError = buildSpeechError("ended-immediately");
            setError(nextError);
            onError?.(nextError);
          }
        }, 160);
        return;
      }

      if (sessionAgeMs < 1200 && !hadRecentFinal) {
        const nextError = buildSpeechError("ended-immediately");
        setError(nextError);
        onError?.(nextError);
      }
    };
    rec.onerror = (event: SpeechRecognitionErrorEvent) => {
      setListening(false);

      const rawCode = String(event.error ?? "unknown").trim().toLowerCase();
      const code = (
        rawCode === "not-allowed" ||
        rawCode === "service-not-allowed" ||
        rawCode === "audio-capture" ||
        rawCode === "no-speech" ||
        rawCode === "aborted"
          ? rawCode
          : "unknown"
      ) as SpeechInputErrorCode;

      const nextError = buildSpeechError(code);
      setError(nextError);
      onError?.(nextError);
    };

    recRef.current = rec;
    return () => {
      try {
        manualStopRef.current = true;
        rec.stop();
      } catch {
        // noop
      }
    };
  }, [lang, onError, onFinal, onInterim, restartOnUnexpectedEnd]);

  const start = useCallback(async (): Promise<SpeechInputStartResult> => {
    const rec = recRef.current;
    if (!rec) {
      const nextError = buildSpeechError("unsupported");
      setError(nextError);
      onError?.(nextError);
      return { ok: false, ...nextError };
    }

    clearError();
    manualStopRef.current = false;
    restartBudgetRef.current = restartOnUnexpectedEnd ? 1 : 0;

    if (preflightMicrophone && navigator.mediaDevices?.getUserMedia) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((track) => track.stop());
      } catch {
        const nextError = buildSpeechError("not-allowed");
        setError(nextError);
        onError?.(nextError);
        return { ok: false, ...nextError };
      }
    }

    try {
      startTsRef.current = Date.now();
      rec.start();
      setListening(true);
      return { ok: true };
    } catch (cause) {
      const nextError = buildSpeechError(
        "start-failed",
        cause instanceof Error && cause.message ? cause.message : undefined,
      );
      setError(nextError);
      onError?.(nextError);
      return { ok: false, ...nextError };
    }
  }, [clearError, onError, preflightMicrophone, restartOnUnexpectedEnd]);

  const stop = useCallback(() => {
    if (!recRef.current) return;
    manualStopRef.current = true;
    recRef.current.stop();
    setListening(false);
  }, []);

  return { supported, listening, error, clearError, start, stop };
}

